// Mancala interface. Tap one of your pits along the bottom to sow its seeds; they go round one pit at a time so you
// can follow them. The computer's pits are along the top, its store on the left and yours on the right. Its last
// move stays marked until yours. Undo takes back your last move and the computer's replies. The starting position is
// in the server HTML (games/boards/_mancala.html.erb), so nothing moves on load.
import { computerMove, CPU_STORE, hintMove, legalMoves, LEVELS, newGame, pitName, sideOf, sow, YOUR_STORE, type Level, type MancalaState, type Sowing } from "./mancala"
import { readJSON, writeJSON } from "../lib/store"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

const SAVE = "dt:save:mancala:v1"
const RULESET = "mancala"
const DOTS = 15 // seeds drawn in a pit; above this only the number shows
interface Saved { v: 1; state: MancalaState; history: MancalaState[]; level: Level; game: number; last: Sowing | null }

export class MancalaGame {
  private s: MancalaState = newGame()
  private history: MancalaState[] = []
  private level: Level = "medium"
  private game = 0
  private last: Sowing | null = null // the computer's last move
  private hint: number | null = null
  private busy = false // sowing on screen, or the computer thinking
  private sentFirst = false
  private hints = 0
  private undos = 0
  private focus = 0
  private shown: number[] | null = null // pit counts mid-sowing
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly board: HTMLElement

  constructor(private readonly root: HTMLElement) {
    this.board = root.querySelector<HTMLElement>("[data-mc-board]")!
    const saved = readJSON<Saved>(SAVE)
    if (saved?.v === 1 && saved.state?.pits?.length === 14) {
      this.s = saved.state; this.history = saved.history ?? []; this.level = LEVELS.includes(saved.level) ? saved.level : "medium"
      this.game = saved.game; this.last = saved.last ?? null
      if (!this.s.over) this.say("Welcome back. Your game is where you left it.")
    } else {
      this.game = newAttempt()
    }
    const select = document.querySelector<HTMLSelectElement>("select[name=mc-level]")
    if (select) {
      select.value = this.level
      select.addEventListener("change", () => {
        this.level = select.value as Level
        this.persist()
        this.say(`Computer: ${select.selectedOptions[0]?.textContent ?? this.level}.`)
      })
    }
    this.board.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("button[data-pit]")
      if (b) this.tap(Number(b.dataset.pit))
    })
    this.board.addEventListener("keydown", (e) => this.onKey(e))
    document.querySelector("[data-toolbar]")?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "undo") this.undo()
      else if (act === "hint") this.showHint()
      else if (act === "new") this.newGame()
    })
    document.addEventListener("keydown", (e) => {
      if ((e.target as HTMLElement).closest?.("input, select, textarea")) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); this.undo() }
      else if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "h") this.showHint()
    })
    this.render()
    if (this.s.turn === "cpu" && !this.s.over) this.computerTurn()
  }

  private get motion(): boolean { return document.documentElement.dataset.motion !== "off" }
  private get pace(): number { return this.motion ? 500 : 150 }

  private persist(): void {
    writeJSON(SAVE, { v: 1, state: this.s, history: this.history.slice(-100), level: this.level, game: this.game, last: this.last } satisfies Saved)
  }

  private tap(p: number): void {
    this.focus = p
    if (this.busy) return this.say(this.s.turn === "cpu" ? "The computer is thinking." : "The seeds are still moving.")
    if (this.s.over) return this.say("This game is over. Choose New game to play again.")
    if (this.s.turn !== "you" || sideOf(p) !== "you") return
    if (!this.s.pits[p]) return this.say(`${cap(pitName(p))} is empty. Choose a pit with seeds in it.`)
    this.history.push(this.s)
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: RULESET, daily: false }) }
    this.hint = null; this.last = null
    this.animate(p, (what) => {
      if (this.s.over) return this.finish(what)
      if (this.s.turn === "you") return this.say(`${what} Your last seed landed in your store, so you go again.`)
      this.say(`${what} The computer is thinking…`)
      this.computerTurn()
    })
  }

  /** Sows pit `from` for the side to move, one seed at a time on screen, then calls `done` with a description. */
  private animate(from: number, done: (what: string) => void): void {
    const before = this.s
    const [next, m] = sow(before, from)
    const who = before.turn
    const frames: number[][] = []
    if (this.motion) {
      const pits = before.pits.slice()
      let seeds = pits[from]!, at = from
      pits[from] = 0
      frames.push(pits.slice())
      while (seeds > 0) {
        at = (at + 1) % 14
        if (at === (who === "you" ? CPU_STORE : YOUR_STORE)) continue
        pits[at]!++; seeds--
        frames.push(pits.slice())
      }
    }
    this.busy = true
    const step = Math.max(70, Math.min(160, 1400 / Math.max(1, frames.length)))
    const tick = (i: number): void => {
      if (this.s !== before) return // New game or Undo meanwhile
      if (i < frames.length) { this.shown = frames[i]!; this.render(); setTimeout(() => tick(i + 1), step); return }
      this.shown = null
      this.s = next
      if (who === "cpu") this.last = m
      this.busy = false
      this.persist()
      this.render()
      const sown = before.pits[from]!
      const mover = who === "you" ? "You" : "The computer"
      let what = `${mover} sowed ${sown} ${sown === 1 ? "seed" : "seeds"} from ${pitName(from)}.`
      if (m.captured) what += ` ${who === "you" ? "You" : "It"} captured ${m.captured} ${m.captured === 1 ? "seed" : "seeds"}.`
      done(what)
    }
    tick(0)
  }

  private computerTurn(): void {
    this.busy = true
    this.render()
    setTimeout(() => {
      const before = this.s
      if (before.turn !== "cpu" || before.over) { this.busy = false; return this.render() }
      const p = computerMove(before, this.level)
      if (this.s !== before) return
      this.animate(p, (what) => {
        if (this.s.over) return this.finish(what)
        if (this.s.turn === "cpu") { this.say(`${what} Its last seed landed in its store, so it goes again…`); return this.computerTurn() }
        this.say(`${what} Your turn.`)
      })
    }, this.pace)
  }

  private finish(what: string): void {
    const o = this.s.over!
    gameEvent({ type: "end", ruleset: RULESET, outcome: o.winner === "you" ? "win" : o.winner === "draw" ? "abandon" : "loss", moves: this.s.moves, hints: this.hints, undos: this.undos, daily: false })
    this.render()
    const end = "One row is empty, so each side banks the seeds left on its side."
    this.say(`${what} ${end} ${o.winner === "you" ? `You won, ${o.you} to ${o.cpu}!` : o.winner === "draw" ? `It's a draw, ${o.you} each.` : `The computer won, ${o.cpu} to ${o.you}. Undo to try again, or start a new game.`}`)
  }

  private undo(): void {
    if (this.busy) return this.say("Wait for the computer's move, then Undo.")
    const prev = this.history.pop()
    if (!prev) return this.say("Nothing to undo.")
    this.undos++
    this.s = prev
    this.hint = null; this.last = null
    this.persist()
    this.render()
    this.say("Your last move and the computer's reply are taken back. Your turn.")
  }

  private showHint(): void {
    if (this.busy || this.s.over || this.s.turn !== "you") return
    const p = hintMove(this.s)
    this.hints++
    this.hint = p
    this.render()
    this.say(`Hint: sow ${pitName(p)}. It's outlined on the board.`)
  }

  private newGame(): void {
    if (this.sentFirst && !this.s.over) gameEvent({ type: "end", ruleset: RULESET, outcome: "new_game", moves: this.s.moves, hints: this.hints, undos: this.undos })
    this.s = newGame()
    this.history = []
    this.game = newAttempt()
    this.hint = null; this.last = null; this.busy = false; this.shown = null
    this.sentFirst = false; this.hints = 0; this.undos = 0
    this.persist()
    this.render()
    this.say("New game. Your pits are along the bottom, and you move first.")
  }

  private onKey(e: KeyboardEvent): void {
    const b = (e.target as HTMLElement).closest<HTMLElement>("button[data-pit]")
    if (!b) return
    const p = Number(b.dataset.pit)
    const d = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0
    if (d) {
      e.preventDefault()
      const to = p + d
      if (to < 0 || to > 5) return
      this.focus = to; this.render()
      this.board.querySelector<HTMLElement>(`button[data-pit="${to}"]`)?.focus()
    }
  }

  private render(): void {
    gameProgress({ key: SAVE, ruleset: RULESET, id: String(this.game), moves: this.s.moves, started: this.s.moves > 0, won: this.s.over?.winner === "you" })
    const pits = this.shown ?? this.s.pits
    const playable = new Set(this.s.turn === "you" && !this.busy ? legalMoves(this.s) : [])
    this.board.querySelectorAll<HTMLElement>("[data-pit]").forEach((el) => {
      const p = Number(el.dataset.pit)
      const n = pits[p]!
      el.innerHTML = `<span class="mc-n">${n}</span>${n && n <= DOTS ? `<span class="mc-seeds" aria-hidden="true">${"<i></i>".repeat(n)}</span>` : ""}`
      el.classList.toggle("is-playable", playable.has(p))
      el.classList.toggle("is-from", this.last?.from === p)
      el.classList.toggle("is-to", this.last?.last === p && !this.shown)
      el.classList.toggle("is-hint", this.hint === p)
      if (el.tagName === "BUTTON") (el as HTMLButtonElement).tabIndex = p === this.focus ? 0 : -1
      const mark = this.last?.from === p ? ", the computer's last move" : this.last?.last === p && !this.shown ? ", where its last seed landed" : ""
      el.setAttribute("aria-label", `${cap(pitName(p))}: ${n} ${n === 1 ? "seed" : "seeds"}${mark}`)
    })
    const score = this.root.querySelector<HTMLElement>("[data-mc-count]")
    if (score) score.textContent = `Your store: ${this.s.pits[YOUR_STORE]} · Computer's: ${this.s.pits[CPU_STORE]}`
    this.root.classList.toggle("is-thinking", this.busy)
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)
